import { FiClock, FiCheck, FiX, FiTrendingUp } from 'react-icons/fi';
import { formatEther } from 'viem';
import { Bet, useIsWinningBet, useCalculatePayout, useClaimReward } from '../../hooks/useContracts';
import { formatNumber } from '../../utils/rangeParser';
import { useEffect } from 'react';
import toast from 'react-hot-toast';
import './BetRow.scss';

interface BetRowProps {
  bet: Bet;
  betId: bigint;
  isResolved: boolean;
  thresholds: {
    threshold1: bigint;
    threshold2: bigint;
    threshold3: bigint;
  };
}

export default function BetRow({ bet, betId, isResolved, thresholds }: BetRowProps) {
  const { isWinning, isLoading: isLoadingWinning } = useIsWinningBet(betId);
  const { payout } = useCalculatePayout(betId);
  const { claimReward, isPending: isClaimPending, isConfirming, isConfirmed, error } = useClaimReward();

  const handleClaim = (e: React.MouseEvent) => {
    e.stopPropagation();
    claimReward(betId);
  };

  useEffect(() => {
    if (isClaimPending) {
      toast.loading('Confirm in your wallet...', { id: 'claim-tx' });
    } else if (isConfirming) {
      toast.loading('Waiting for confirmation...', { id: 'claim-tx' });
    } else if (isConfirmed) {
      toast.success('Reward claimed successfully!', { id: 'claim-tx' });
    } else if (error) {
      toast.error(`Claim failed`, { id: 'claim-tx' });
    }
  }, [isClaimPending, isConfirming, isConfirmed, error]);

  let status: 'pending' | 'won' | 'lost' = 'pending';
  if (isResolved && !isLoadingWinning) {
    status = isWinning ? 'won' : 'lost';
  }

  const canClaim = status === 'won' && !bet.claimed;

  const getRangeText = () => {
    const { threshold1, threshold2, threshold3 } = thresholds;
    const index = Number(bet.thresholdIndex);
    if (index === 0) return `0 - ${formatNumber(threshold1)}`;
    if (index === 1) return `${formatNumber(threshold1)} - ${formatNumber(threshold2)}`;
    if (index === 2) return `${formatNumber(threshold2)} - ${formatNumber(threshold3)}`;
    if (index === 3) return `${formatNumber(threshold3)}+`;
    return 'Unknown';
  };

  return (
    <div className="bet-row">
      <div className="bet-row__header">
        <div className="bet-row__range">
          <span className="bet-row__range-label">Prediction:</span>
          {getRangeText()}
        </div>

        <div className="bet-row__actions">
          {canClaim ? (
            <button
              className="btn btn-sm btn-claim"
              onClick={handleClaim}
              disabled={isClaimPending || isConfirming}
            >
              {isClaimPending ? 'Confirm...' : isConfirming ? 'Confirming...' : 'Claim'}
            </button>
          ) : (
            <div className={`bet-row__status bet-row__status--${status}`}>
              {status === 'pending' && <FiClock />}
              {status === 'won' && <FiCheck />}
              {status === 'lost' && <FiX />}
              <span>{status}</span>
            </div>
          )}
        </div>
      </div>

      <div className="bet-row__footer">
        <div className="bet-row__amount">
          Wager: {formatEther(bet.amount)} ETH
        </div>

        <div className="bet-row__payout">
          {status === 'won' && payout !== undefined && payout > 0n && (
            <>
              <FiTrendingUp />
              {formatEther(payout)} ETH
            </>
          )}
          {status === 'pending' && (
            <span className="bet-row__payout--potential">Potential payout pending</span>
          )}
        </div>
      </div>
    </div>
  );
}
